import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_VAT_RATE_BPS, vatBreakdown } from '@growhero/shared';
import { cn } from '@/lib/cn';
import { formatMoney, formatPercent } from '@/lib/format';
import { usePlans } from './api';

/**
 * VAT display (ADR-B10). Catalog prices are NET: they read «19 € + ΦΠΑ», with the gross amount
 * on a secondary line «23,56 € με ΦΠΑ 24%». Amounts that were actually charged (invoices,
 * purchases, the checkout total) already include VAT and say so.
 */

/** "24%", "13,5%" in the UI language. */
export function formatVatRate(rateBps: number): string {
  return formatPercent(rateBps / 10_000, 1);
}

/** Platform VAT rate from the public catalog (falls back to the seed rate while loading). */
export function useVatRate(): number {
  const plans = usePlans();
  return plans.data?.vatRateBps ?? DEFAULT_VAT_RATE_BPS;
}

export interface VatLabels {
  /** "19 €" (net, compact for whole euros). */
  net: string;
  /** "19 € + ΦΠΑ" */
  plusVat: string;
  /** "23,56 €" */
  gross: string;
  /** "23,56 € με ΦΠΑ 24%" */
  withVat: string;
  /** "24%" */
  rate: string;
}

/** Formatter for net catalog prices: `labels(1900)` → «19 € + ΦΠΑ» / «23,56 € με ΦΠΑ 24%». */
export function useVatLabels(rateBps?: number) {
  const { t } = useTranslation('common');
  const live = useVatRate();
  const bps = rateBps ?? live;
  return useCallback(
    (netCents: number, currency = 'EUR'): VatLabels => {
      const b = vatBreakdown(netCents, bps);
      const net = formatMoney(b.netCents, currency, { compact: true });
      const gross = formatMoney(b.grossCents, currency);
      const rate = formatVatRate(bps);
      return {
        net,
        plusVat: t('vat.plusVat', { amount: net }),
        gross,
        withVat: t('vat.withVat', { amount: gross, rate }),
        rate,
      };
    },
    [bps, t],
  );
}

/**
 * Inline price for lists and buttons: «19 € + ΦΠΑ» and, below or beside it, the gross line.
 * `suffix` (e.g. «/ μήνα») follows the net amount.
 */
export function NetPrice({
  cents,
  currency,
  rateBps,
  suffix,
  layout = 'stack',
  className,
}: {
  cents: number;
  currency?: string;
  rateBps?: number;
  suffix?: string;
  layout?: 'stack' | 'inline';
  className?: string;
}) {
  const v = useVatLabels(rateBps)(cents, currency);
  return (
    <span className={cn(layout === 'stack' ? 'inline-grid' : 'inline', className)}>
      <span>
        <span className="num font-semibold text-ink">{v.plusVat}</span>
        {suffix && <span className="text-ink-muted"> {suffix}</span>}
      </span>
      <span className={cn('num text-xs text-ink-subtle', layout === 'inline' && 'ml-1.5')}>
        {v.withVat}
      </span>
    </span>
  );
}
